// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';
import { wrapApiError, wrapCaughtError, ApiError } from '@/utils/errors/apiErrorWrapper';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_DATA_URL;
const API_ENDPOINT = '/api/workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_DATA_SCOPE;

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface LinkFileToAskProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  body: {
    dataRoomFileId: string;
    instanceId: string;
  };
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
export const linkFileToAsk = async ({
  getAccessToken,
  workspaceId,
  body,
}: LinkFileToAskProps): Promise<void> => {
  try {
    console.log('🔄 Linking file to ask session...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/linkfile/ask`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token),
      body: JSON.stringify(body),
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      // Use comprehensive error extraction to get all error details
      throw await wrapApiError(response, convertURI, 'POST');
    }
    console.log('✅ File linked to ask session successfully.');

    return;
  } catch (error) {
    console.error('🔴 Error linking file to ask session:', error);

    // If it's already an ApiError, re-throw it (it has all the info)
    if (error instanceof ApiError) {
      throw error;
    }

    // Otherwise, wrap it to extract all available information
    if (error instanceof Error) {
      const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/linkfile/ask`;
      throw await wrapCaughtError(error, undefined, encodeURI(url), 'POST');
    }

    throw error;
  }
};
